import {
  parseEntityRef,
  RELATION_CONSUMES_API,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import {
  Content,
  FavoriteToggleIcon,
  PageWithHeader,
  TableColumn,
} from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { CatalogTable, CatalogTableRow } from '@backstage/plugin-catalog';
import {
  EntityListProvider,
  EntityRefLink,
  useEntityList,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import { isTechDocsAvailable } from '@backstage/plugin-techdocs';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import {
  getEntityLifecycle,
  getExplorerTableSx,
  renderLifecycleBadge,
} from '../entity-explorer/shared';
import { shellMetrics } from '../../theme/tokens';
import { CatalogIndexControls } from './index-page/CatalogIndexControls';
import { CatalogOverviewCards } from './index-page/CatalogOverviewCards';
import {
  catalogIndexAvailableColumns,
  catalogIndexDefaultVisibleColumns,
  CatalogIndexColumnId,
  useCatalogIndexState,
} from './index-page/useCatalogIndexState';

type CatalogTableColumnConfig = {
  column: TableColumn<CatalogTableRow>;
  id: CatalogIndexColumnId;
};

const getApiDefinitionPath = (entity: CatalogTableRow['entity']) => {
  const entityNamespace = entity.metadata.namespace ?? 'default';
  const entityKind = entity.kind.toLocaleLowerCase('en-US');

  if (entityKind === 'api') {
    return `/catalog/${entityNamespace}/${entityKind}/${entity.metadata.name}/definition`;
  }

  const relatedApiRef =
    entity.relations?.find(relation => relation.type === RELATION_PROVIDES_API)
      ?.targetRef ??
    entity.relations?.find(relation => relation.type === RELATION_CONSUMES_API)
      ?.targetRef ??
    (Array.isArray(entity.spec?.providesApis)
      ? entity.spec?.providesApis?.[0]
      : undefined) ??
    (Array.isArray(entity.spec?.consumesApis)
      ? entity.spec?.consumesApis?.[0]
      : undefined);

  if (!relatedApiRef) {
    return undefined;
  }

  const parsedApiRef = parseEntityRef(String(relatedApiRef), {
    defaultKind: 'API',
    defaultNamespace: entityNamespace,
  });

  return `/catalog/${parsedApiRef.namespace.toLocaleLowerCase(
    'en-US',
  )}/${parsedApiRef.kind.toLocaleLowerCase('en-US')}/${parsedApiRef.name}/definition`;
};

const getCatalogColumnConfigs = (kind?: string): CatalogTableColumnConfig[] => {
  const baseColumns: CatalogTableColumnConfig[] = [
    {
      id: 'name',
      column: {
        title: 'Name',
        highlight: true,
        width: '16%',
        customFilterAndSearch: (filter, { entity }) =>
          entity.metadata.name
            .toLocaleLowerCase()
            .includes(filter.toLocaleLowerCase()),
        render: ({ entity }) => (
          <EntityRefLink
            defaultKind={kind}
            entityRef={entity}
            title={entity.metadata.name}
          />
        ),
      },
    },
    {
      id: 'description',
      column: {
        title: 'Description',
        width: '25%',
        customFilterAndSearch: (filter, { entity }) =>
          entity.metadata.description
            ?.toLocaleLowerCase()
            .includes(filter.toLocaleLowerCase()) || false,
        render: ({ entity }) => entity.metadata.description ?? '—',
      },
    },
  ];

  if (kind === 'component' || !kind) {
    return [
      ...baseColumns,
      {
        id: 'system',
        column: { ...CatalogTable.columns.createSystemColumn(), width: '12%' },
      },
      {
        id: 'owner',
        column: { ...CatalogTable.columns.createOwnerColumn(), width: '12%' },
      },
      {
        id: 'type',
        column: {
          ...CatalogTable.columns.createSpecTypeColumn(),
          width: '11%',
        },
      },
      {
        id: 'lifecycle',
        column: {
          title: 'Lifecycle',
          field: 'entity.spec.lifecycle',
          width: '12%',
          render: ({ entity }) =>
            renderLifecycleBadge(getEntityLifecycle(entity)),
        },
      },
    ];
  }

  switch (kind) {
    case 'user':
      return [
        ...baseColumns,
        {
          id: 'namespace',
          column: CatalogTable.columns.createNamespaceColumn(),
        },
      ];
    case 'domain':
    case 'system':
      return [
        ...baseColumns,
        {
          id: 'owner',
          column: CatalogTable.columns.createOwnerColumn(),
        },
      ];
    case 'group':
      return [
        ...baseColumns,
        {
          id: 'type',
          column: CatalogTable.columns.createSpecTypeColumn(),
        },
        {
          id: 'namespace',
          column: CatalogTable.columns.createNamespaceColumn(),
        },
      ];
    case 'template':
      return [
        ...baseColumns,
        {
          id: 'type',
          column: CatalogTable.columns.createSpecTypeColumn(),
        },
      ];
    case 'location':
      return [
        ...baseColumns,
        {
          id: 'type',
          column: CatalogTable.columns.createSpecTypeColumn(),
        },
        {
          id: 'targets',
          column: CatalogTable.columns.createSpecTargetsColumn(),
        },
      ];
    default:
      return [
        ...baseColumns,
        {
          id: 'system',
          column: CatalogTable.columns.createSystemColumn(),
        },
        {
          id: 'owner',
          column: CatalogTable.columns.createOwnerColumn(),
        },
        {
          id: 'type',
          column: CatalogTable.columns.createSpecTypeColumn(),
        },
        {
          id: 'lifecycle',
          column: CatalogTable.columns.createSpecLifecycleColumn(),
        },
      ];
  }
};

const CustomCatalogTable = ({
  columns,
  pageSize,
}: {
  columns: TableColumn<CatalogTableRow>[];
  pageSize: number;
}) => {
  const theme = useTheme();
  const configApi = useApi(configApiRef);
  const { isStarredEntity, toggleStarredEntity } = useStarredEntities();

  const openAppRoute = (path: string) => {
    const appBaseUrl = configApi.getOptionalString('app.baseUrl');
    if (!appBaseUrl) {
      window.location.assign(path);
      return;
    }

    const targetUrl = new URL(path.replace(/^\/+/, ''), `${appBaseUrl}/`);
    window.location.assign(targetUrl.toString());
  };

  const actions = useMemo(
    () => [
      ({ entity }: CatalogTableRow) => {
        const namespace = entity.metadata.namespace ?? 'default';
        const kind = entity.kind.toLocaleLowerCase('en-US');
        const name = entity.metadata.name;
        const hasTechDocs = isTechDocsAvailable(entity);

        return {
          disabled: !hasTechDocs,
          icon: () => (
            <TableActionIcon
              disabled={!hasTechDocs}
              icon={<MenuBookOutlinedIcon fontSize="small" />}
              label="TechDocs"
            />
          ),
          onClick: () => openAppRoute(`/docs/${namespace}/${kind}/${name}`),
          tooltip: hasTechDocs ? 'Open TechDocs' : 'No TechDocs available',
        };
      },
      ({ entity }: CatalogTableRow) => {
        const apiDefinitionPath = getApiDefinitionPath(entity);
        const hasOpenApiDocs = Boolean(apiDefinitionPath);

        return {
          disabled: !hasOpenApiDocs,
          icon: () => (
            <TableActionIcon
              disabled={!hasOpenApiDocs}
              icon={<DescriptionOutlinedIcon fontSize="small" />}
              label="OpenAPI"
            />
          ),
          onClick: () => apiDefinitionPath && openAppRoute(apiDefinitionPath),
          tooltip: hasOpenApiDocs
            ? 'Open API definition'
            : 'No OpenAPI definition available',
        };
      },
      ({ entity }: CatalogTableRow) => {
        const namespace = entity.metadata.namespace ?? 'default';
        const kind = entity.kind.toLocaleLowerCase('en-US');
        const name = entity.metadata.name;

        return {
          icon: () => (
            <TableActionIcon
              icon={<OpenInNewOutlinedIcon fontSize="small" />}
              label="View"
            />
          ),
          onClick: () => openAppRoute(`/catalog/${namespace}/${kind}/${name}`),
          tooltip: 'Open entity',
        };
      },
      ({ entity }: CatalogTableRow) => {
        const isStarred = isStarredEntity(entity);

        return {
          icon: () => (
            <TableActionIcon
              active={isStarred}
              icon={<FavoriteToggleIcon isFavorite={isStarred} />}
              label="Star"
            />
          ),
          onClick: () => toggleStarredEntity(entity),
          tooltip: isStarred ? 'Remove star' : 'Star entity',
        };
      },
    ],
    [configApi, isStarredEntity, toggleStarredEntity],
  );

  return (
    <CatalogTable
      actions={actions}
      tableOptions={{
        actionsCellStyle: {
          color: theme.palette.text.secondary,
          textAlign: 'center',
        },
        actionsColumnIndex: -1,
        emptyRowsWhenPaging: false,
        pageSize,
        pageSizeOptions: [10, 20, 50],
        padding: 'dense',
        search: false,
        toolbar: false,
      }}
      columns={columns}
    />
  );
};

const TableActionIcon = ({
  active = false,
  disabled = false,
  icon,
  label,
}: {
  active?: boolean;
  disabled?: boolean;
  icon: React.ReactNode;
  label: string;
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        alignItems: 'center',
        color: disabled
          ? alpha(theme.palette.text.secondary, 0.24)
          : active
            ? theme.palette.warning.main
            : theme.palette.text.secondary,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        lineHeight: 1,
        minWidth: 52,
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'inline-flex',
          filter: disabled ? 'grayscale(1)' : 'none',
          height: 20,
          justifyContent: 'center',
          opacity: disabled ? 0.6 : 1,
          '& svg': {
            fontSize: 18,
          },
        }}
      >
        {icon}
      </Box>
      <Typography
        component="span"
        sx={{
          color: 'inherit',
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: '0.01em',
          lineHeight: 1.1,
          opacity: disabled ? 0.72 : 1,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const CatalogIndexContent = () => {
  const theme = useTheme();
  const { filters } = useEntityList();
  const {
    pageSize,
    resetVisibleColumns,
    searchText,
    setSearchText,
    toggleVisibleColumn,
    visibleColumns,
  } = useCatalogIndexState({
    availableColumns: catalogIndexAvailableColumns,
    defaultVisibleColumns: catalogIndexDefaultVisibleColumns,
  });

  const columnConfigs = useMemo(
    () => getCatalogColumnConfigs(filters.kind?.value),
    [filters.kind?.value],
  );
  const displayedColumnConfigs = useMemo(() => {
    const selected = columnConfigs.filter(({ id }) =>
      visibleColumns.includes(id),
    );
    return selected.length ? selected : columnConfigs;
  }, [columnConfigs, visibleColumns]);
  const linkColumnIndices = useMemo(
    () =>
      displayedColumnConfigs.reduce<number[]>((indices, { id }, index) => {
        if (id === 'name' || id === 'system' || id === 'owner') {
          indices.push(index + 1);
        }
        return indices;
      }, []),
    [displayedColumnConfigs],
  );

  return (
    <Stack spacing={2}>
      <CatalogIndexControls
        columnIds={columnConfigs.map(({ id }) => id)}
        resetVisibleColumns={resetVisibleColumns}
        searchText={searchText}
        setSearchText={setSearchText}
        toggleVisibleColumn={toggleVisibleColumn}
        visibleColumns={visibleColumns}
      />

      <Box
        sx={{
          ...getExplorerTableSx(theme, {
            linkColumnIndices,
          }),
          '& td:last-child > div': {
            gap: theme.spacing(0.25),
            justifyContent: 'center',
          },
          '& td:last-child button': {
            borderRadius: `${theme.shape.borderRadius * 1.5}px`,
            padding: theme.spacing(0.75, 0.5),
          },
          '& th:last-child': { textAlign: 'center' },
        }}
      >
        <CustomCatalogTable
          columns={displayedColumnConfigs.map(({ column }) => column)}
          pageSize={pageSize}
        />
      </Box>
    </Stack>
  );
};

export const CustomCatalogPage = () => {
  const orgName =
    useApi(configApiRef).getOptionalString('organization.name') ?? 'Backstage';

  return (
    <PageWithHeader
      subtitle="Discover entities, owners, templates, and lifecycle data across engineering."
      title={`${orgName} Catalog`}
      themeId="home"
    >
      <Content>
        <Box
          sx={{
            boxSizing: 'border-box',
            maxWidth: 'none',
            px: { xs: 2, md: 3 },
            width: '100%',
          }}
        >
          <Stack spacing={shellMetrics.sectionGap / 8}>
            <CatalogOverviewCards />

            <EntityListProvider pagination={{ limit: 10, mode: 'offset' }}>
              <CatalogIndexContent />
            </EntityListProvider>
          </Stack>
        </Box>
      </Content>
    </PageWithHeader>
  );
};
